import React, { useState, useEffect } from 'react';
import { X, Save, Shield, AlertTriangle, Star, Cpu, Plus, Trash2, RefreshCw } from 'lucide-react';
import { User } from '../../types/auth';
import { userService } from '../../services/database/userService';
import { roleService } from '../../services/auth/RoleService';
import { toast } from '../../utils/toast';

interface UserEditModalProps {
  user: User;
  onClose: () => void;
  onSave: () => void;
}

/**
 * User Edit Modal
 * Modal chỉnh sửa thông tin user, role và permissions
 */
const UserEditModal: React.FC<UserEditModalProps> = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    role: user.role,
    isActive: user.isActive,
    isVerified: user.isVerified,
    permissions: user.permissions || {},
  });
  
  const [customPermissions, setCustomPermissions] = useState<string[]>([]);
  const [useCustomPermissions, setUseCustomPermissions] = useState(false);
  const [saving, setSaving] = useState(false);

  // Lab Devices State
  const [devices, setDevices] = useState<any[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [newMac, setNewMac] = useState('');
  const [newDeviceName, setNewDeviceName] = useState('');
  const [addingDevice, setAddingDevice] = useState(false);
  const [previewSecureId, setPreviewSecureId] = useState<string | null>(null);
  const [computingPreview, setComputingPreview] = useState(false);

  // Sync computeSecureId for list display
  const [sidMap, setSidMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (devices.length > 0) {
      devices.forEach(d => {
        if (!sidMap[d.mac_address]) {
          const clean = d.mac_address.replace(/[:-]/g, '').toUpperCase();
          userService.computeSecureId(clean).then(sid => {
            if (sid) setSidMap(prev => ({ ...prev, [d.mac_address]: sid }));
          });
        }
      });
    }
  }, [devices]);

  const allRoles = roleService.getAllRoles();
  const allPermissions = roleService.getAllPermissions();

  // Get default permissions for selected role
  const rolePermissions = roleService.getRolePermissions(formData.role);

  // Group permissions by resource
  const permissionsByResource = allPermissions.reduce((acc, permission) => {
    if (!acc[permission.resource]) {
      acc[permission.resource] = [];
    }
    acc[permission.resource].push(permission);
    return acc;
  }, {} as Record<string, typeof allPermissions>);

  // Load devices on mount
  useEffect(() => {
    loadDevices();
  }, [user.id]);

  const loadDevices = async () => {
    try {
      setLoadingDevices(true);
      const data = await userService.getUserDevices(user.id);
      setDevices(data);
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleComputePreview = async () => {
    if (!newMac) return;
    try {
      setComputingPreview(true);
      const sid = await userService.computeSecureId(newMac.toUpperCase());
      setPreviewSecureId(sid);
    } catch (error) {
      toast.error('Lỗi khi tính toán mã');
    } finally {
      setComputingPreview(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Đã sao chép mã thiết bị!');
  };

  const handleAddDevice = async () => {
    if (!newMac.trim()) {
      toast.error('Vui lòng nhập mã MAC');
      return;
    }

    // Regex check MAC (XX:XX:XX:XX:XX:XX or XXXXXXXXXXXX)
    const macRegex = /^([0-9A-Fa-f]{2}[:-]?){5}([0-9A-Fa-f]{2})$/;
    if (!macRegex.test(newMac)) {
      toast.error('Mã MAC không hợp lệ (VD: AA:BB:CC:DD:EE:FF)');
      return;
    }

    try {
      setAddingDevice(true);
      await userService.bindDevice(user.id, newMac.toUpperCase(), newDeviceName || undefined);
      toast.success('Gán thiết bị thành công');
      setNewMac('');
      setNewDeviceName('');
      loadDevices();
    } catch (error: any) {
      toast.error(error.message || 'Lỗi khi gán thiết bị');
    } finally {
      setAddingDevice(false);
    }
  };

  const handleRemoveDevice = async (deviceId: number) => {
    if (!confirm('Bạn có chắc chắn muốn gỡ thiết bị này?')) return;

    try {
      await userService.unbindDevice(deviceId);
      toast.success('Đã gỡ thiết bị');
      loadDevices();
    } catch (error) {
      toast.error('Lỗi khi gỡ thiết bị');
    }
  };

  const handleRoleChange = (newRole: string) => {
    setFormData({ ...formData, role: newRole as any });
    if (!useCustomPermissions) {
      setCustomPermissions(roleService.getRolePermissions(newRole));
    }
  };

  const handlePermissionToggle = (permissionId: string) => {
    if (customPermissions.includes(permissionId)) {
      setCustomPermissions(customPermissions.filter(p => p !== permissionId));
    } else {
      setCustomPermissions([...customPermissions, permissionId]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Tên không được để trống');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Email không được để trống');
      return;
    }

    try {
      setSaving(true);

      // Determine final permissions
      const finalRBAC = useCustomPermissions
        ? customPermissions
        : roleService.getRolePermissions(formData.role);

      // Merge with existing permissions (PRO, etc)
      const finalPermissions = {
        ...formData.permissions,
        capabilities: finalRBAC
      };

      await userService.updateUser(user.id, {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        permissions: finalPermissions,
        isActive: formData.isActive,
        isVerified: formData.isVerified,
        // Auto-approve teacher if role is set to teacher
        teacherInfo: formData.role === 'teacher'
          ? { ...user.teacherInfo, isApproved: true } as any
          : user.teacherInfo,
        updatedAt: new Date(),
      });

      toast.success('Cập nhật người dùng thành công');
      onSave();
      onClose();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Không thể cập nhật người dùng');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700 border-red-300';
      case 'teacher': return 'bg-purple-100 text-purple-700 border-purple-300';
      case 'student': return 'bg-green-100 text-green-700 border-green-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Chỉnh sửa người dùng</h2>
            <p className="text-sm text-gray-600 mt-1">ID: {user.id}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin cơ bản</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Họ và tên <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập họ tên"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="email@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Role Selection */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                Vai trò và quyền hạn
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vai trò <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {allRoles.map((role) => (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => handleRoleChange(role.id)}
                        className={`
                          p-4 border-2 rounded-lg transition text-left
                          ${formData.role === role.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                      >
                        <div className={`text-sm font-bold mb-1 ${getRoleBadgeColor(role.id)} inline-block px-2 py-1 rounded`}>
                          {role.name}
                        </div>
                        <p className="text-xs text-gray-600 mt-2">{role.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {role.permissions.length} quyền
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Permissions Toggle */}
                <div className="bg-white border border-gray-300 rounded-xl p-4 shadow-sm">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useCustomPermissions}
                      onChange={(e) => {
                        setUseCustomPermissions(e.target.checked);
                        if (!e.target.checked) {
                          setCustomPermissions(roleService.getRolePermissions(formData.role));
                        }
                      }}
                      className="mt-1 w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-bold text-gray-900 flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-1 text-amber-500" />
                        Tùy chỉnh quyền hạn (Nâng cao)
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        Bật tùy chọn này để cấu hình quyền chi tiết thay vì dùng quyền mặc định của vai trò
                      </p>
                    </div>
                  </label>
                </div>

                {/* Permissions List */}
                {useCustomPermissions && (
                  <div className="border border-gray-200 bg-white rounded-xl p-5 shadow-sm">
                    <h4 className="text-sm font-black text-gray-900 mb-4 uppercase tracking-tight border-b pb-2">
                      Quyền hạn chi tiết ({customPermissions.length} quyền đã chọn)
                    </h4>
                    <div className="space-y-6 max-h-64 overflow-y-auto pr-2">
                      {Object.entries(permissionsByResource).map(([resource, permissions]) => (
                        <div key={resource}>
                          <p className="text-[10px] font-black text-blue-600 uppercase mb-3 tracking-widest bg-blue-50 px-2 py-0.5 rounded inline-block">
                            {resource}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {permissions.map((permission) => (
                              <label
                                key={permission.id}
                                className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition"
                              >
                                <input
                                  type="checkbox"
                                  checked={customPermissions.includes(permission.id)}
                                  onChange={() => handlePermissionToggle(permission.id)}
                                  className="mt-1 w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-900">
                                    {permission.name}
                                  </span>
                                  <p className="text-[10px] text-gray-500 leading-tight mt-0.5">{permission.description}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Default Permissions Display */}
                {!useCustomPermissions && (
                  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <p className="text-xs font-bold text-gray-500 uppercase mb-3 tracking-tight">
                      Quyền mặc định của vai trò ({rolePermissions.length} quyền):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {rolePermissions.map((permId) => {
                        const perm = roleService.getPermission(permId);
                        return perm ? (
                          <span
                            key={permId}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-[10px] font-bold rounded border border-gray-200"
                          >
                            {perm.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status Toggles */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-lg font-black text-gray-900 mb-4 flex items-center border-b pb-2">Trạng thái tài khoản</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <label className="flex items-center space-x-3 cursor-pointer p-2 hover:bg-gray-50 rounded-lg transition">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm font-bold text-gray-900">Tài khoản hoạt động</span>
                    <p className="text-xs text-gray-500">Người dùng có thể đăng nhập</p>
                  </div>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer p-2 hover:bg-gray-50 rounded-lg transition">
                  <input
                    type="checkbox"
                    checked={formData.isVerified}
                    onChange={(e) => setFormData({ ...formData, isVerified: e.target.checked })}
                    className="w-5 h-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm font-bold text-gray-900">Email đã xác thực</span>
                    <p className="text-xs text-gray-500">Đã kiểm tra email chính chủ</p>
                  </div>
                </label>
              </div>
            </div>

            {/* PRO Membership Toggle */}
            <div className="bg-white border-2 border-purple-100 rounded-2xl p-6 shadow-sm">
              <h4 className="text-lg font-black text-gray-900 mb-6 flex items-center uppercase tracking-tight">
                <Star className="w-6 h-6 mr-2 text-amber-500 fill-current" />
                Quản lý Gói thành viên PRO
                {(user.permissions?.pro_request?.status === 'pending') && (
                  <span className="ml-3 px-3 py-1 bg-amber-100 text-amber-700 text-[10px] font-black rounded-full animate-pulse uppercase border border-amber-200">
                    Có yêu cầu kích hoạt
                  </span>
                )}
              </h4>

              {user.permissions?.pro_request && (
                <div className="mb-6 text-xs bg-gray-50 border border-gray-200 p-4 rounded-xl">
                  <div className="grid grid-cols-2 gap-4">
                    <p className="flex flex-col gap-1">
                      <span className="text-gray-500 font-bold uppercase text-[9px]">Gói yêu cầu:</span>
                      <span className="font-black text-blue-700 text-sm">{user.permissions.pro_request.plan}</span>
                    </p>
                    <p className="flex flex-col gap-1">
                      <span className="text-gray-500 font-bold uppercase text-[9px]">Mã giao dịch:</span>
                      <code className="text-purple-600 font-black text-sm">{user.permissions.pro_request.transaction_code}</code>
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Ebook PRO Toggle */}
                <div className="p-4 rounded-xl border-2 border-gray-100 bg-white hover:border-purple-200 transition-colors">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!formData.permissions?.pro}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          permissions: { ...prev.permissions, pro: e.target.checked }
                        }));
                      }}
                      className="mt-1 w-6 h-6 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-black text-gray-900 block uppercase">Ebook PRO</span>
                      <p className="text-[10px] text-gray-500 mt-1 leading-tight">Truy cập toàn bộ bài học, trắc nghiệm và AI.</p>
                    </div>
                  </label>
                </div>

                {/* Lab PRO Toggle */}
                <div className="p-4 rounded-xl border-2 border-gray-100 bg-white hover:border-blue-200 transition-colors">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!formData.permissions?.lab_pro}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          permissions: { ...prev.permissions, lab_pro: e.target.checked }
                        }));
                      }}
                      className="mt-1 w-6 h-6 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-black text-gray-900 block uppercase">Lab PRO</span>
                      <p className="text-[10px] text-gray-500 mt-1 leading-tight">Đo lường, kết nối thiết bị và phân tích dữ liệu IoT.</p>
                    </div>
                  </label>
                </div>

                {/* English PRO Toggle */}
                <div className="p-4 rounded-xl border-2 border-gray-100 bg-white hover:border-emerald-200 transition-colors">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!formData.permissions?.eng_pro}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          permissions: { ...prev.permissions, eng_pro: e.target.checked }
                        }));
                      }}
                      className="mt-1 w-6 h-6 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-black text-gray-900 block uppercase">English PRO</span>
                      <p className="text-[10px] text-gray-500 mt-1 leading-tight">Toàn bộ khóa học Vật lý Tiếng Anh và luyện tập.</p>
                    </div>
                  </label>
                </div>

                {/* PMaker PRO Toggle */}
                <div className="p-4 rounded-xl border-2 border-gray-100 bg-white hover:border-purple-200 transition-colors">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!formData.permissions?.pmaker_pro}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          permissions: { ...prev.permissions, pmaker_pro: e.target.checked }
                        }));
                      }}
                      className="mt-1 w-6 h-6 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-black text-gray-900 block uppercase">PMaker PRO</span>
                      <p className="text-[10px] text-gray-500 mt-1 leading-tight">Công cụ thiết kế ma trận đề thi và ngân hàng câu hỏi.</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* ✅ Lab Device Management Section - CLEAN LIGHT UI */}
            {(formData.role === 'teacher' || formData.role === 'admin') && (
              <div className="mt-8 p-6 border border-gray-200 bg-white rounded-2xl shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-50 rounded-xl text-blue-600 border border-blue-100">
                      <Cpu className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="text-lg font-black text-gray-900 uppercase tracking-tighter">Hệ thống Thiết bị Lab IoT</h4>
                      <p className="text-xs text-blue-600 font-bold uppercase opacity-80">Quản lý định danh & Mã bảo mật luồng dữ liệu</p>
                    </div>
                  </div>
                  <button 
                    type="button"
                    onClick={loadDevices}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-gray-600 text-xs font-black rounded-xl hover:bg-gray-50 transition border border-gray-200 shadow-sm"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingDevices ? 'animate-spin' : ''}`} />
                    TẢI LẠI DANH SÁCH
                  </button>
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                  <table className="w-full text-left text-sm border-collapse min-w-[600px]">
                    <thead className="bg-gray-50 text-gray-700 uppercase font-black text-[10px] tracking-widest border-b border-gray-200">
                      <tr>
                        <th className="px-5 py-4 border-r border-gray-200">Tên Bộ KIT / Vị trí</th>
                        <th className="px-5 py-4 border-r border-gray-200">Mã MAC (Phần cứng)</th>
                        <th className="px-5 py-4 border-r border-gray-200">Mã Thiết bị (Secure ID)</th>
                        <th className="px-5 py-4 text-center w-32">Thao tác</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {/* --- EXISTING DEVICES --- */}
                      {devices.length > 0 ? (
                        devices.map((device) => (
                          <tr key={device.id} className="hover:bg-blue-50/20 transition-all group">
                            <td className="px-5 py-4 border-r border-gray-100 font-bold text-gray-900">
                              {device.device_name || 'BỘ THÍ NGHIỆM'}
                            </td>
                            <td className="px-5 py-4 border-r border-gray-100">
                              <div className="flex items-center justify-between group/mac">
                                <code className="bg-gray-50 text-gray-900 px-2 py-1 rounded text-xs font-black font-mono border border-gray-200 tracking-tight">
                                  {device.mac_address}
                                </code>
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(device.mac_address)}
                                  className="opacity-0 group-hover/mac:opacity-100 p-1.5 text-gray-400 hover:text-blue-600 transition"
                                  title="Copy MAC"
                                >
                                  <Save className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                            <td className="px-5 py-4 border-r border-gray-100">
                              <div className="flex items-center justify-between gap-4 group/sid">
                                <code className="text-blue-700 font-black font-mono text-xs break-all tracking-wider selection:bg-blue-200">
                                  {sidMap[device.mac_address] || device.secure_id || 'ĐANG TÍNH...'}
                                </code>
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(sidMap[device.mac_address] || device.secure_id)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-600 hover:text-white rounded-lg transition shadow-sm active:scale-95"
                                  title="Copy Mã gửi Giáo viên"
                                >
                                  <Save className="w-3.5 h-3.5" />
                                  <span className="text-[10px] font-black uppercase">COPY</span>
                                </button>
                              </div>
                            </td>
                            <td className="px-5 py-4 text-center">
                              <button
                                type="button"
                                onClick={() => handleRemoveDevice(device.id)}
                                className="flex items-center justify-center gap-1.5 mx-auto px-3 py-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition font-black text-[10px] uppercase border border-transparent hover:border-rose-100"
                              >
                                <Trash2 className="w-4 h-4" />
                                Gỡ bỏ
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : !loadingDevices && (
                        <tr>
                          <td colSpan={4} className="px-5 py-16 text-center text-gray-400 italic">
                            Hiện chưa có bộ thiết bị nào được gán cho giáo viên này.
                          </td>
                        </tr>
                      )}

                      {/* --- ADD NEW DEVICE ROW --- */}
                      <tr className="bg-gray-50/50 border-t-2 border-gray-200">
                        <td className="px-4 py-5 border-r border-gray-200">
                          <input
                            type="text"
                            placeholder="Ví dụ: Lớp 11A1"
                            value={newDeviceName}
                            onChange={(e) => setNewDeviceName(e.target.value)}
                            className="w-full text-xs px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50 outline-none font-black text-gray-900 transition-all bg-white"
                          />
                        </td>
                        <td className="px-4 py-5 border-r border-gray-200">
                          <div className="space-y-2">
                            <input
                              type="text"
                              placeholder="MAC (12 ký tự hex)"
                              value={newMac}
                              onChange={(e) => {
                                const val = e.target.value.toUpperCase();
                                setNewMac(val);
                                const cleanMac = val.replace(/[:-]/g, '');
                                if (cleanMac.length === 12) {
                                  userService.computeSecureId(cleanMac).then(setPreviewSecureId);
                                } else {
                                  setPreviewSecureId(null);
                                }
                              }}
                              className="w-full text-xs px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-50 outline-none font-mono font-black text-gray-900 tracking-widest transition-all bg-white"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-5 border-r border-gray-200">
                          {previewSecureId ? (
                            <div className="p-3 bg-white border-2 border-blue-500 rounded-xl text-blue-700 shadow-md animate-in zoom-in-95 slide-in-from-right-4 duration-300">
                              <div className="flex items-center justify-between gap-3 mb-1">
                                <span className="text-[9px] font-black uppercase text-blue-500 tracking-tighter">Mã bảo mật vừa sinh:</span>
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(previewSecureId)}
                                  className="p-1 hover:bg-blue-50 rounded-md transition text-blue-600"
                                  title="Copy ngay"
                                >
                                  <Save className="w-4 h-4" />
                                </button>
                              </div>
                              <code className="text-xs font-black font-mono break-all leading-tight">{previewSecureId}</code>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2 p-3 text-[10px] text-gray-400 font-black italic border-2 border-dashed border-gray-200 rounded-xl uppercase">
                              <Star className="w-4 h-4" />
                              Nhập đủ MAC để sinh mã
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-5 text-center">
                          <button
                            type="button"
                            onClick={handleAddDevice}
                            disabled={addingDevice || !newMac || newMac.replace(/[:-]/g, '').length < 12}
                            className="w-full py-3.5 bg-blue-600 text-white text-xs font-black uppercase rounded-xl hover:bg-blue-700 disabled:opacity-30 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-100 hover:-translate-y-0.5 active:scale-95"
                          >
                            {addingDevice ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-5 h-5" />}
                            THÊM THIẾT BỊ
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 p-4 bg-gray-50 text-gray-700 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                      <Star className="w-5 h-5 fill-current" />
                    </div>
                    <div>
                      <p className="text-xs font-black uppercase tracking-tight mb-1 text-gray-900">Hướng dẫn sử dụng hệ thống bảo mật:</p>
                      <ul className="text-[11px] font-bold space-y-1.5 text-gray-600 leading-tight">
                        <li className="flex items-center gap-2">
                           <span className="w-1.5 h-1.5 bg-blue-400 rounded-full shrink-0"></span>
                           Nhập mã MAC của chip ESP32 (12 ký tự hex) vào cột thứ 2 ở hàng cuối cùng.
                        </li>
                        <li className="flex items-center gap-2">
                           <span className="w-1.5 h-1.5 bg-blue-400 rounded-full shrink-0"></span>
                           Mã thiết bị sẽ <strong className="text-blue-600">tự động sinh ra</strong> ở cột 3. Thầy có thể <strong>Copy</strong> gửi cho giáo viên ngay.
                        </li>
                        <li className="flex items-center gap-2">
                           <span className="w-1.5 h-1.5 bg-blue-400 rounded-full shrink-0"></span>
                           Bấm <strong>THÊM THIẾT BỊ</strong> để lưu. Một giáo viên có thể dùng bao nhiêu bộ KIT tùy ý.
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>


          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Đang lưu...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Lưu thay đổi</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div >
    </div >
  );
};

export default UserEditModal;
